import { useState, useEffect } from "react";

import useOnUnmount from "../../../hooks/useOnUnmount";

import CampaignMembersActions from "../CampaignMembers.actions";

import {
  ROLE_OWNER,
  ROLE_RECRUITER,
  ROLE_SUBSCRIBER_ATS,
  ROLE_SUBSCRIBER_CAMPAIGN
} from "../CampaignMembers.constants";

function useCampaignMembers({ campaignId, campaignMembers, setChangesHaveBeenMade }) {
  const [ownerId, setOwnerId] = useState();
  const [recruiterId, setRecruiterId] = useState();
  const [subscribersCampaignIds, setSubscribersCampaignIds] = useState([]);
  const [subscribersAtsIds, setSubscribersAtsIds] = useState([]);
  const [addedSubscribersAts, setAddedSubscribersAts] = useState([]);
  const [addedSubscribersCampaign, setAddedSubscribersCampaign] = useState([]);

  useEffect(() => {
    if (campaignId) {
      CampaignMembersActions.getCampaignMembers(campaignId);
    }
  }, [campaignId]);

  useEffect(() => {
    if (!campaignMembers) return;

    const newOwner = campaignMembers.find((member) => member.roles?.includes(ROLE_OWNER));
    setOwnerId(newOwner?.id);

    const newRecruiter = campaignMembers.find((member) => member.roles?.includes(ROLE_RECRUITER));
    setRecruiterId(newRecruiter?.id);

    const subscribersCampaignIds = campaignMembers
      .filter((member) => member.roles?.includes(ROLE_SUBSCRIBER_CAMPAIGN))
      .map((member) => member.id);

    setSubscribersCampaignIds(subscribersCampaignIds);

    const subscribersAtsIds = campaignMembers
      .filter((member) => member.roles?.includes(ROLE_SUBSCRIBER_ATS))
      .map((member) => member.id);

    setSubscribersAtsIds(subscribersAtsIds);
  }, [campaignMembers]);

  useOnUnmount(() => {
    CampaignMembersActions.clear();
  });

  function onChangeOwner(newOwner) {
    if (ownerId !== newOwner.value) {
      setOwnerId(newOwner.value);
      setChangesHaveBeenMade(true);
    }
  }

  function onChangeRecruiter(newRecruiter) {
    if (recruiterId !== newRecruiter.value) {
      setRecruiterId(newRecruiter.value);
      setChangesHaveBeenMade(true);
    }
  }

  /**
   * Updates the list of ATS subscribers and identifies which subscribers have been added.
   *
   * It is important to understand that this function is being called every time ONE subscriber is selected.
   *
   * whenever a subscriber is added, it is saved in the addedSubscribersATS state.
   *
   * @param {Array} subscribers - The current list of subscribers in the selector.
   */
  function onChangeSubscribersAts(subscribers) {
    // Filter out subscribers that have been deleted.
    const currentAddedSubscribers = addedSubscribersAts.filter((addedSubscriber) =>
      subscribers.some((subscriber) => subscriber.value === addedSubscriber.value)
    );

    // Get the new subscriber that was added.
    const newSubscriber = subscribers.find(
      (subscriber) => !subscribersAtsIds.includes(subscriber.value)
    );

    // Combine the updated list with new additions.
    const updatedAddedSubscribers = [...currentAddedSubscribers];
    if (newSubscriber) {
      updatedAddedSubscribers.push(newSubscriber);
    }
    setAddedSubscribersAts(updatedAddedSubscribers);

    // Update subscribers IDs based on the current subscribers list.
    const subscribersIds = subscribers.map((subscriber) => subscriber.value);
    setSubscribersAtsIds(subscribersIds);

    setChangesHaveBeenMade(true);
  }

  /**
   * Updates the list of Campaign subscribers and identifies which subscribers have been added.
   *
   * It is important to understand that this function is being called every time ONE subscriber is selected.
   *
   * whenever a subscriber is added, it is saved in the addedSubscribersCampaign state.
   *
   * @param {Array} subscribers - The current list of subscribers in the selector.
   */
  function onChangeSubscribersCampaign(subscribers) {
    // Filter out subscribers that have been deleted.
    const currentAddedSubscribers = addedSubscribersCampaign.filter((addedSubscriber) =>
      subscribers.some((subscriber) => subscriber.value === addedSubscriber.value)
    );

    // Identify new subscribers to add.
    const newSubscriber = subscribers.find(
      (subscriber) => !subscribersCampaignIds.includes(subscriber.value)
    );

    // Combine the updated list with new additions.
    const updatedAddedSubscribers = [...currentAddedSubscribers];
    if (newSubscriber) {
      updatedAddedSubscribers.push(newSubscriber);
    }
    setAddedSubscribersCampaign(updatedAddedSubscribers);

    // Update subscribers IDs based on the current subscribers list.
    const subscribersIds = subscribers.map((subscriber) => subscriber.value);
    setSubscribersCampaignIds(subscribersIds);

    setChangesHaveBeenMade(true);
  }

  return {
    onChangeOwner,
    onChangeRecruiter,
    onChangeSubscribersAts,
    onChangeSubscribersCampaign,
    ownerId,
    recruiterId,
    subscribersAtsIds,
    subscribersCampaignIds,
    addedSubscribersAts,
    addedSubscribersCampaign
  };
}

export default useCampaignMembers;
